import { useEffect, useRef, useState } from "react";
import { DEV_LOG_STREAMS, masterSocketUrl, type DevLogEvent, type DevLogStreamId } from "../api";

const MAX_PER_STREAM = 2500;
const MAX_ALL = 5000;

interface WsMessage {
  type: string;
  streams?: DevLogStreamId[];
  entries?: DevLogEvent[];
  entry?: DevLogEvent;
  message?: string;
}

function emptyBuffers(): Record<DevLogStreamId, DevLogEvent[]> {
  return { api: [], tcp: [], udp: [], mediamtx: [], rtsp: [] };
}

function wsUrl(): string {
  const explicit = import.meta.env.VITE_LOGS_WS_URL as string | undefined;
  return masterSocketUrl("/ws/logs", explicit);
}

function trim(list: DevLogEvent[], max: number): DevLogEvent[] {
  return list.length > max ? list.slice(list.length - max) : list;
}

export function useDevLogs() {
  const [byStream, setByStream] = useState<Record<DevLogStreamId, DevLogEvent[]>>(emptyBuffers);
  const [all, setAll] = useState<DevLogEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let flushTimer: ReturnType<typeof setInterval> | null = null;
    let delay = 1000;
    const pending: DevLogEvent[] = [];

    const applyBatch = (batch: DevLogEvent[], replace: boolean) => {
      if (replace) {
        const next = emptyBuffers();
        for (const event of batch) {
          if (!DEV_LOG_STREAMS.includes(event.stream)) continue;
          next[event.stream].push(event);
        }
        for (const stream of DEV_LOG_STREAMS) next[stream] = trim(next[stream], MAX_PER_STREAM);
        setByStream(next);
        setAll(trim(batch, MAX_ALL));
        return;
      }
      if (batch.length === 0) return;
      setByStream((prev) => {
        const next = { ...prev };
        for (const event of batch) {
          if (!DEV_LOG_STREAMS.includes(event.stream)) continue;
          next[event.stream] = trim(next[event.stream].concat(event), MAX_PER_STREAM);
        }
        return next;
      });
      setAll((prev) => trim(prev.concat(batch), MAX_ALL));
    };

    const flush = () => {
      if (pending.length === 0) return;
      applyBatch(pending.splice(0), false);
    };

    const connect = () => {
      if (stopped) return;
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;
      ws.onopen = () => {
        delay = 1000;
        setConnected(true);
        ws.send(JSON.stringify({ action: "subscribe" }));
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(String(ev.data)) as WsMessage;
          if (msg.type === "snapshot" && Array.isArray(msg.entries)) {
            pending.length = 0;
            applyBatch(msg.entries, true);
            return;
          }
          if (msg.type === "log" && msg.entry) pending.push(msg.entry);
        } catch {
          /* ignore */
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (stopped) return;
        retry = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 15_000);
      };
    };

    flushTimer = setInterval(flush, 80);
    connect();
    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      if (flushTimer) clearInterval(flushTimer);
      flush();
      const socket = wsRef.current;
      wsRef.current = null;
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        socket.close();
      }
    };
  }, []);

  return { byStream, all, connected };
}
